import React from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { WandIcon } from '../components/icons/WandIcon';
import { AI_ACCENT } from './aiTheme';

// Explicit request: AI processing shouldn't read as "just another spinner" - a sonar-style
// pulsing ring behind a gently wiggling wand, distinct from CircularProgress everywhere else in
// the app, so anywhere it appears (per-document button, panel button, minimized bar pill) reads
// unmistakably as "the AI is working on this" rather than a generic loading state. Kept as a
// bespoke CSS animation (same reasoning as YourEnquiriesWidget.tsx's own iconBreathe/pulseTrace
// keyframes) rather than AnimatedItem, which is for one-shot mount transitions, not a looping
// "in progress" indicator.
const useStyles = makeStyles(() =>
    createStyles({
        root: {
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        ring: {
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: `2px solid ${AI_ACCENT}`,
            animation: '$aiPing 1500ms cubic-bezier(0, 0, 0.2, 1) infinite',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                opacity: 0.35,
            },
        },
        ringDelayed: {
            animationDelay: '750ms',
        },
        '@keyframes aiPing': {
            '0%': { transform: 'scale(0.65)', opacity: 0.7 },
            '75%, 100%': { transform: 'scale(1.55)', opacity: 0 },
        },
        icon: {
            position: 'relative',
            color: AI_ACCENT,
            display: 'flex',
            animation: '$aiWandWiggle 1500ms ease-in-out infinite',
            '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
            },
        },
        '@keyframes aiWandWiggle': {
            '0%, 100%': { transform: 'rotate(-12deg) scale(1)' },
            '50%': { transform: 'rotate(12deg) scale(1.18)' },
        },
    })
);

interface AiProcessingIndicatorProps {
    /** Overall bounding box, in px - the wand icon itself is sized proportionally. */
    size?: number;
}

export const AiProcessingIndicator: React.FC<AiProcessingIndicatorProps> = ({ size = 20 }) => {
    const classes = useStyles();

    return (
        <span className={classes.root} style={{ width: size, height: size }}>
            <span className={classes.ring} />
            <span className={`${classes.ring} ${classes.ringDelayed}`} />
            <WandIcon className={classes.icon} style={{ fontSize: Math.round(size * 0.55) }} />
        </span>
    );
};
